// HomeScreen.js — JOBENG Full-Featured Home Dashboard
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Image, ScrollView,
  StatusBar, Animated, Dimensions, Platform, Alert, Modal,
  Switch, TextInput, RefreshControl, ActivityIndicator, Linking,
  Share,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// API KEYS
// ─────────────────────────────────────────────────────────────────────────────
const GUARDIAN_KEY  = '96d3065f-e31b-47fe-80d5-96a2664509e9';
const REED_KEY      = 'c49ba09d-1dbc-4854-b73b-88f0fc74e7d2';
const APP_VERSION   = '2.0.0';
const JOBENG_API    = 'https://api.jobeng.ai';
const JOBENG_KEY    = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const SECRET_CODE   = 'Sesysy@2024'; // ← Change in production

// ─────────────────────────────────────────────────────────────────────────────
// FEATURED CAROUSEL DATA
// ─────────────────────────────────────────────────────────────────────────────
const FEATURED_SLIDES = [
  {
    id: 'summit',
    title: '🇬🇧 UK Engineering Summit 2025',
    description: 'Connect with top UK employers and industry leaders. Limited spots available.',
    bg: ['#0e4e96', '#1a6ab5'],
    buttonLabel: 'Register Now',
    route: 'Events',
    icon: 'calendar-star',
    badge: 'LIVE',
    badgeColor: '#ef4444',
  },
  {
    id: 'ai',
    title: '🤖 AI Career Coach',
    description: 'Get personalised career advice powered by Claude AI — free for all JOBENG members.',
    bg: ['#19abb5', '#0e9aa7'],
    buttonLabel: 'Try Now',
    route: 'UKInsights',
    icon: 'brain',
    badge: 'NEW',
    badgeColor: '#10b981',
  },
  {
    id: 'salary',
    title: '💷 Salary Insights 2025',
    description: 'Is your pay competitive? Check live UK engineering salary benchmarks instantly.',
    bg: ['#1e3a5f', '#0e4e96'],
    buttonLabel: 'Check Salary',
    route: 'SalaryComparison',
    icon: 'currency-gbp',
    badge: 'HOT',
    badgeColor: '#fdb913',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// QUICK ACTIONS
// ─────────────────────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { id: 'jobs',     label: 'Find Jobs',      icon: 'briefcase-search',      color: '#0e4e96', route: 'SearchJobsScreen' },
  { id: 'salary',   label: 'Salary Tool',    icon: 'currency-gbp',          color: '#10b981', route: 'SalaryComparison' },
  { id: 'insights', label: 'UK Insights',    icon: 'chart-line',            color: '#19abb5', route: 'UKInsights' },
  { id: 'events',   label: 'Events',         icon: 'calendar-month',        color: '#fdb913', route: 'Events' },
  { id: 'news',     label: 'News',           icon: 'newspaper-variant',     color: '#8b5cf6', route: 'NewsList' },
  { id: 'academy',  label: 'Academy',        icon: 'school',                color: '#f59e0b', route: 'LearningLoginScreen' },
  { id: 'network',  label: 'Network',        icon: 'account-group',         color: '#ef4444', route: 'ProfessionalLogin' },
  { id: 'business', label: 'For Business',   icon: 'domain',                color: '#6366f1', route: 'BusinessLogin' },
];

// ─────────────────────────────────────────────────────────────────────────────
// TRENDING SKILLS
// ─────────────────────────────────────────────────────────────────────────────
const TRENDING_SKILLS = [
  { name: 'AI / ML',          growth: '+45%', color: '#0e4e96', icon: 'brain' },
  { name: 'Renewable Energy', growth: '+38%', color: '#10b981', icon: 'solar-power' },
  { name: 'Robotics',         growth: '+28%', color: '#8b5cf6', icon: 'robot-industrial' },
  { name: 'Cybersecurity',    growth: '+25%', color: '#ef4444', icon: 'shield-check' },
  { name: 'BIM',              growth: '+20%', color: '#f59e0b', icon: 'home-city' },
  { name: 'Cloud / AWS',      growth: '+35%', color: '#19abb5', icon: 'cloud-outline' },
];

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATED COUNTER
// ─────────────────────────────────────────────────────────────────────────────
function AnimatedCounter({ target, suffix = '', color = '#0e4e96', size = 22 }) {
  const anim = useRef(new Animated.Value(0)).current;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    Animated.timing(anim, { toValue: target, duration: 1800, useNativeDriver: false }).start();
    anim.addListener(({ value }) => setDisplay(Math.round(value)));
    return () => anim.removeAllListeners();
  }, [target]);

  return (
    <Text style={{ fontSize: size, fontWeight: '800', color }}>{display.toLocaleString()}{suffix}</Text>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LIVE DOT
// ─────────────────────────────────────────────────────────────────────────────
function LiveDot({ color = '#10b981' }) {
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1.7, duration: 700, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 1,   duration: 700, useNativeDriver: true }),
    ])).start();
  }, []);
  return <Animated.View style={[styles.liveDot, { backgroundColor: color, transform: [{ scale: pulse }] }]} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// STAGGERED CARD WRAPPER
// ─────────────────────────────────────────────────────────────────────────────
function StaggerCard({ children, delay = 0 }) {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, { toValue: 1, duration: 500, delay, useNativeDriver: true }).start();
  }, []);
  return (
    <Animated.View style={{ opacity: anim, transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [30, 0] }) }] }}>
      {children}
    </Animated.View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURED CAROUSEL
// ─────────────────────────────────────────────────────────────────────────────
function FeaturedCarousel({ onNavigate }) {
  const [active, setActive] = useState(0);
  const fade = useRef(new Animated.Value(1)).current;
  const translateX = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const interval = setInterval(() => {
      Animated.timing(fade, { toValue: 0, duration: 250, useNativeDriver: true }).start(() => {
        setActive(i => (i + 1) % FEATURED_SLIDES.length);
        translateX.setValue(30);
        Animated.parallel([
          Animated.timing(fade,       { toValue: 1,  duration: 350, useNativeDriver: true }),
          Animated.timing(translateX, { toValue: 0,  duration: 350, useNativeDriver: true }),
        ]).start();
      });
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const slide = FEATURED_SLIDES[active];

  return (
    <View style={styles.carouselContainer}>
      <Animated.View style={[styles.carouselCard, { opacity: fade, transform: [{ translateX }] }]}>
        <View style={[styles.carouselBg, { backgroundColor: slide.bg[0] }]}>
          <View style={styles.carouselInner}>
            <View style={{ flex: 1 }}>
              <View style={styles.carouselTopRow}>
                <View style={[styles.carouselBadge, { backgroundColor: slide.badgeColor }]}>
                  <Text style={styles.carouselBadgeText}>{slide.badge}</Text>
                </View>
                <View style={styles.carouselDots}>
                  {FEATURED_SLIDES.map((_, i) => (
                    <TouchableOpacity key={i} onPress={() => setActive(i)}>
                      <View style={[styles.carouselDot, i === active && styles.carouselDotActive]} />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <Text style={styles.carouselTitle}>{slide.title}</Text>
              <Text style={styles.carouselDescription}>{slide.description}</Text>
              <TouchableOpacity style={styles.carouselButton} onPress={() => onNavigate(slide.route)}>
                <Text style={styles.carouselButtonText}>{slide.buttonLabel}</Text>
                <MaterialIcons name="arrow-forward" size={14} color="#fff" />
              </TouchableOpacity>
            </View>
            <View style={styles.carouselIconWrap}>
              <MaterialCommunityIcons name={slide.icon} size={64} color="rgba(255,255,255,0.25)" />
            </View>
          </View>
        </View>
      </Animated.View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function HomeScreen() {
  const navigation = useNavigation();

  // ── Ticker ─────────────────────────────────────────────────────────────────
  const scrollX = useRef(new Animated.Value(0)).current;
  const tickerAnim = useRef(null);

  // ── Fade-in ────────────────────────────────────────────────────────────────
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── UI State ───────────────────────────────────────────────────────────────
  const [refreshing,       setRefreshing]       = useState(false);
  const [searchQuery,      setSearchQuery]       = useState('');
  const [searchFocused,    setSearchFocused]     = useState(false);

  // ── Live Data ──────────────────────────────────────────────────────────────
  const [liveJobs,         setLiveJobs]          = useState([]);
  const [liveNews,         setLiveNews]          = useState([]);
  const [liveStats,        setLiveStats]         = useState({ jobs: 15247, companies: 2480, success: 98, engineers: 650000 });
  const [aiInsight,        setAiInsight]         = useState(null);
  const [alertCount,       setAlertCount]        = useState(0);
  const [tickerItems,      setTickerItems]        = useState([
    '🚀 15,000+ UK Engineering Jobs Available Now',
    '💷 Avg Engineering Salary Hits £52,500 in 2025',
    '🎓 50% Off All Academy Courses This Month',
    '🤖 AI Engineering Roles Up 45% Year-on-Year',
    '🇬🇧 UK Engineering Summit 2025 — Register Now',
    '🌱 Renewable Energy Sector Fastest Growing in UK',
  ]);
  const [isLive,           setIsLive]            = useState(false);
  const [lastUpdated,      setLastUpdated]        = useState('');
  const [userName,         setUserName]           = useState('');

  // ── Developer ──────────────────────────────────────────────────────────────
  const [devMode,          setDevMode]           = useState(false);
  const [showDevPanel,     setShowDevPanel]       = useState(false);
  const [tapCount,         setTapCount]           = useState(0);
  const [devSettings, setDevSettings] = useState({
    showAPILogs: false, mockDataEnabled: false, bypassAPIErrors: false,
    slowNetworkSimulation: false, offlineMode: false, bypassAuth: false,
    autoLogin: false, skipOnboarding: false, disableTokenRefresh: false,
    debugMode: false, showBoundingBoxes: false, disableAnimations: false,
    showPerformanceMetrics: false, forceRTL: false, clearCacheOnStart: false,
    persistLogs: false, enableDataExport: false, enableBetaFeatures: false,
    enableExperimentalUI: false, disableAnalytics: false, disableCrashReporting: false,
    enableTestMode: false, mockPayments: false, skipValidation: false,
  });

  // ── Ticker loop ────────────────────────────────────────────────────────────
  const startTicker = useCallback(() => {
    scrollX.setValue(width);
    tickerAnim.current = Animated.loop(
      Animated.timing(scrollX, { toValue: -tickerItems.join(' · ').length * 8.5, duration: 22000, useNativeDriver: true })
    );
    tickerAnim.current.start();
  }, [tickerItems]);

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 900, useNativeDriver: true }).start();
    startTicker();
    loadAllData();
    loadUser();
    loadDevSettings();
  }, []);

  useFocusEffect(useCallback(() => { loadAllData(); }, []));

  // ── Alert badge = number of news items newer than the last one the user saw ──
  // News is ordered newest-first; we count from the top until we hit the
  // last-seen item. Tapping the bell stores the current top and clears the badge,
  // so the number always matches what's actually on the NewsList screen.
  useEffect(() => {
    (async () => {
      try {
        if (!Array.isArray(liveNews) || liveNews.length === 0) { setAlertCount(0); return; }
        const sigOf = (n) => String(n?.id ?? n?.title ?? '');
        const lastSeen = (await AsyncStorage.getItem('news_last_seen_top')) || '';
        if (!lastSeen) { setAlertCount(liveNews.length); return; } // first ever visit
        let count = 0;
        for (const n of liveNews) {
          if (sigOf(n) === lastSeen) break;
          count++;
        }
        setAlertCount(count);
      } catch {
        setAlertCount(0);
      }
    })();
  }, [liveNews]);

  const loadUser = async () => {
    try {
      const name = await AsyncStorage.getItem('userName');
      if (name) setUserName(name);
    } catch { /* silent */ }
  };

  const loadDevSettings = async () => {
    try {
      const saved = await AsyncStorage.getItem('devSettings');
      if (saved) setDevSettings(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch { /* silent */ }
  };

  // ── Fetch all data — DB first, then live APIs ─────────────────────────────
  const loadAllData = async () => {
    try {
      // ── Step 1: Load from JOBENG DB instantly ──────────────────────────────
      try {
        const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
        const uid = await AsyncStorage.getItem('user_id') || '';
        const dbRes = await fetch(
          `${JOBENG_API}/api/home/all${uid ? `?user_id=${uid}` : ''}`,
          { headers: { 'x-api-key': JOBENG_KEY, 'Authorization': `Bearer ${tok}` } }
        );
        if (dbRes.ok) {
          const dbData = await dbRes.json();
          if (dbData.success && dbData.data) {
            const d = dbData.data;
            if (d.liveStats)                    setLiveStats(d.liveStats);
            if (d.liveJobs?.length)             setLiveJobs(d.liveJobs);
            if (d.liveNews?.length)             setLiveNews(d.liveNews);
            if (d.tickerItems?.length)          setTickerItems(d.tickerItems);
            if (d.aiInsight)                    setAiInsight(d.aiInsight);
            if (d.userConfig?.user_name)        setUserName(d.userConfig.user_name);
            setIsLive(true);
            setLastUpdated(d.lastUpdated || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
          }
        }
      } catch (dbErr) {
        console.warn('[HomeScreen] DB load failed (offline?):', dbErr.message);
      }

      // ── Step 2: Upgrade with live external APIs (best effort) ─────────────
      const [jobs, news, insight] = await Promise.allSettled([
        fetchLiveJobs(),
        fetchLiveNews(),
        fetchAIInsight(),
      ]);
      if (jobs.status    === 'fulfilled' && jobs.value?.length) {
        setLiveJobs(jobs.value);
      }
      if (news.status    === 'fulfilled' && news.value?.length) {
        setLiveNews(news.value);
        const tickerFromNews = news.value.slice(0, 4).map(n => `📰 ${n.title}`);
        setTickerItems(prev => [...tickerFromNews, ...prev.slice(4)]);
        // Cache fresh news to DB in background
        fetch(`${JOBENG_API}/api/home/news/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': JOBENG_KEY },
          body: JSON.stringify({ guardian_key: GUARDIAN_KEY }),
        }).catch(() => {});
      }
      if (insight.status === 'fulfilled' && insight.value) setAiInsight(insight.value);
      setIsLive(true);
      setLastUpdated(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      setIsLive(false);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchLiveJobs = async () => {
    const url = `https://www.reed.co.uk/api/1.0/search?keywords=engineering&locationName=United+Kingdom&resultsToTake=5`;
    const res  = await fetch(url, {
      headers: { Authorization: `Basic ${btoa(REED_KEY + ':')}` },
    });
    const json = await res.json();
    return (json.results || []).map(j => ({
      id:       j.jobId,
      title:    j.jobTitle,
      company:  j.employerName,
      location: j.locationName,
      salary:   j.minimumSalary ? `£${(j.minimumSalary / 1000).toFixed(0)}k–£${(j.maximumSalary / 1000).toFixed(0)}k` : 'Competitive',
      posted:   j.date,
      url:      j.jobUrl,
    }));
  };

  const fetchLiveNews = async () => {
    const res  = await fetch(`https://content.guardianapis.com/search?q=engineering+UK&show-fields=trailText,thumbnail&page-size=6&order-by=newest&api-key=${GUARDIAN_KEY}`);
    const json = await res.json();
    return (json.response?.results || []).map(a => ({
      id:          a.id,
      title:       a.webTitle,
      snippet:     a.fields?.trailText || '',
      source:      'The Guardian',
      category:    a.sectionName,
      publishDate: new Date(a.webPublicationDate),
      imageUrl:    a.fields?.thumbnail,
      readTime:    4,
      priority:    a.webTitle?.toLowerCase().includes('breaking') ? 'breaking' : 'medium',
    }));
  };

  const fetchAIInsight = async () => {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: `You are a UK engineering careers expert. Give ONE concise, specific, actionable career insight for today ${new Date().toDateString()} for UK engineers (max 2 sentences). Then give ONE short recommended action (max 8 words). Format as JSON: {"insight": "...", "action": "...", "icon": "MaterialCommunityIcons icon name", "color": "#hex"}`,
        }],
      }),
    });
    const data = await res.json();
    const t    = data.content?.[0]?.text || '';
    return JSON.parse(t.replace(/```json|```/g, '').trim());
  };

  const onRefresh = () => { setRefreshing(true); loadAllData(); };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const navigate = (route) => navigation.navigate(route);
  // Live Job Feed funnels unauthenticated visitors to professional sign-up.
  // NOTE: confirm this matches the Stack.Screen name registered in App.js.
  const PROFESSIONAL_SIGNUP_ROUTE = 'ProfessionalSignup';
  const formatDate = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1)  return 'Just now';
    if (h < 24) return `${h}h ago`;
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const handleSearch = () => {
    if (searchQuery.trim()) navigation.navigate('SearchJobsScreen', { query: searchQuery.trim() });
  };

  const handleShare = async () => {
    await Share.share({ message: 'Find top UK engineering jobs and insights on JOBENG — the UK\'s premier engineering platform. Download now!', title: 'JOBENG' });
  };

  // Open the notifications/news list, mark everything as seen, clear the badge.
  const handleOpenNotifications = async () => {
    try {
      const top = Array.isArray(liveNews) && liveNews.length
        ? String(liveNews[0]?.id ?? liveNews[0]?.title ?? '')
        : '';
      if (top) await AsyncStorage.setItem('news_last_seen_top', top);
    } catch { /* silent */ }
    setAlertCount(0);
    navigation.navigate('NewsList');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DEVELOPER PANEL
  // ─────────────────────────────────────────────────────────────────────────
  const handleLogoTap = async () => {
    const newCount = tapCount + 1;
    setTapCount(newCount);
    if (newCount >= 7) {
      setTapCount(0);
      const isDev = await checkIfDeveloper();
      if (isDev) { setDevMode(true); setShowDevPanel(true); Alert.alert('🔓 Developer Mode', 'Welcome back, developer!'); }
      else         Alert.alert('🔒 Access Denied', 'Invalid developer code');
    }
  };

  const checkIfDeveloper = () => new Promise(resolve => {
    Alert.prompt('🔐 Developer Access', 'Enter developer code:', [
      { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
      { text: 'OK', onPress: (code) => { code === SECRET_CODE ? resolve(true) : (Alert.alert('❌ Wrong Code'), resolve(false)); } },
    ], 'plain-text');
  });

  const toggleDevSetting = (key) => setDevSettings(prev => ({ ...prev, [key]: !prev[key] }));

  const saveDevSettings = async () => {
    try {
      await AsyncStorage.setItem('devSettings', JSON.stringify(devSettings));
      Alert.alert('✅ Saved', 'Developer settings saved');
      setShowDevPanel(false);
    } catch { Alert.alert('❌ Error', 'Failed to save settings'); }
  };

  const clearAllData = () => Alert.alert('⚠️ Clear All Data', 'Delete all local data?', [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Clear', style: 'destructive', onPress: async () => { await AsyncStorage.clear(); Alert.alert('✅ Done', 'Restart the app.'); } },
  ]);

  const viewStorageData = async () => {
    const keys = await AsyncStorage.getAllKeys();
    const data = await AsyncStorage.multiGet(keys);
    const str  = data.map(([k, v]) => `${k}: ${v?.substring(0, 50)}`).join('\n\n');
    Alert.alert('📦 Storage Data', str || 'No data');
  };

  const exportLogs = async () => {
    const logs = await AsyncStorage.getItem('appLogs');
    Alert.alert('📋 Logs', logs ? logs.substring(0, 500) : 'No logs found');
  };

  const testAPIConnection = async () => {
    Alert.alert('🔄 Testing...', 'Checking connections...');
    try {
      const [g, r] = await Promise.all([
        fetch(`https://content.guardianapis.com/search?api-key=${GUARDIAN_KEY}&page-size=1`),
        fetch(`https://www.reed.co.uk/api/1.0/search?resultsToTake=1`, { headers: { Authorization: `Basic ${btoa(REED_KEY + ':')}` } }),
      ]);
      Alert.alert('✅ API Status', `Guardian: ${g.ok ? '✅' : '❌'}\nReed: ${r.ok ? '✅' : '❌'}`);
    } catch (e) { Alert.alert('❌ Error', e.message); }
  };

  const shareDebugInfo = () => Alert.alert('📋 Debug Info', `JOBENG v${APP_VERSION}\nPlatform: ${Platform.OS} ${Platform.Version}\nTime: ${new Date().toLocaleString()}\nLive: ${isLive}\nLast update: ${lastUpdated}`);

  const forceCrash = () => Alert.alert('⚠️ Force Crash', 'Crash for testing?', [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Crash', style: 'destructive', onPress: () => { throw new Error('Intentional crash'); } },
  ]);

  const resetToFactory = () => Alert.alert('🔄 Factory Reset', 'Reset all data?', [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Reset', style: 'destructive', onPress: async () => { await AsyncStorage.clear(); Alert.alert('✅ Done', 'Restart the app.'); } },
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  const tickerString = tickerItems.join('   •   ');

  return (
    <>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <View style={styles.headerBackground}>
        <View style={styles.headerTopRow}>
          {/* Logo — tap 7× for dev mode */}
          <TouchableOpacity onPress={handleLogoTap} activeOpacity={0.9} style={styles.logoWrap}>
            <Image source={require('../assets/jobeng_icon.png')} style={styles.logo} resizeMode="contain" />
          </TouchableOpacity>

          <View style={styles.headerCenterCol}>
            <Text style={styles.title}>JOBENG</Text>
            <View style={styles.liveRow}>
              <LiveDot color={isLive ? '#10b981' : '#f59e0b'} />
              <Text style={styles.liveLabel}>{isLive ? `LIVE · ${lastUpdated}` : 'CONNECTING...'}</Text>
            </View>
          </View>

          <View style={styles.headerRight}>
            {/* Notification bell */}
            <TouchableOpacity style={styles.headerIconBtn} onPress={handleOpenNotifications}>
              <MaterialCommunityIcons name="bell-outline" size={22} color="#fff" />
              {alertCount > 0 && (
                <View style={styles.notifBadge}>
                  <Text style={styles.notifBadgeText}>{alertCount > 9 ? '9+' : alertCount}</Text>
                </View>
              )}
            </TouchableOpacity>
            {/* Share */}
            <TouchableOpacity style={styles.headerIconBtn} onPress={handleShare}>
              <MaterialCommunityIcons name="share-variant-outline" size={22} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        <Text style={styles.subtitle}>UK's Premier AI-Powered Engineering Platform</Text>

        {/* Search bar */}
        <View style={styles.searchWrap}>
          <View style={[styles.searchBar, searchFocused && styles.searchBarFocused]}>
            <MaterialIcons name="search" size={20} color={searchFocused ? '#0e4e96' : '#9ca3af'} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search jobs, companies, skills..."
              placeholderTextColor="#9ca3af"
              value={searchQuery}
              onChangeText={setSearchQuery}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onSubmitEditing={handleSearch}
              returnKeyType="search"
            />
            {searchQuery ? (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={16} color="#9ca3af" />
              </TouchableOpacity>
            ) : null}
          </View>
          {searchQuery ? (
            <TouchableOpacity style={styles.searchBtn} onPress={handleSearch}>
              <Text style={styles.searchBtnText}>Search</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {/* News ticker */}
        <View style={styles.tickerWrap}>
          <View style={styles.tickerLabel}>
            <LiveDot color="#fdb913" />
            <Text style={styles.tickerLabelText}>LIVE</Text>
          </View>
          <View style={styles.tickerTrack}>
            <Animated.Text
              style={[styles.tickerText, { transform: [{ translateX: scrollX }] }]}
              numberOfLines={1}
            >
              {tickerString}{'   '}
            </Animated.Text>
          </View>
        </View>
      </View>

      {/* ── MAIN SCROLL ────────────────────────────────────────────────────── */}
      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}
      >
        <Animated.View style={{ opacity: fadeAnim }}>

          {/* Welcome card */}
          <StaggerCard delay={0}>
            <View style={styles.welcomeCard}>
              <View style={styles.welcomeLeft}>
                <Text style={styles.welcomeGreeting}>
                  {userName ? `Welcome back, ${userName}! 👋` : 'Welcome to JOBENG 👋'}
                </Text>
                <Text style={styles.welcomeSubtext}>
                  Find top engineering opportunities across the UK
                </Text>
                <View style={styles.welcomeTagRow}>
                  {['🇬🇧 UK-Focused', '🤖 AI-Powered', '⚡ Live Data'].map(t => (
                    <View key={t} style={styles.welcomeTag}><Text style={styles.welcomeTagText}>{t}</Text></View>
                  ))}
                </View>
              </View>
              <View style={styles.welcomeIconWrap}>
                <MaterialCommunityIcons name="engine" size={40} color="#0e4e96" />
              </View>
            </View>
          </StaggerCard>

          {/* Live stats strip */}
          <StaggerCard delay={100}>
            <View style={styles.statsCard}>
              {[
                { label: 'UK Jobs',      value: liveStats.jobs,      suffix: '+',  color: '#0e4e96' },
                { label: 'Companies',    value: liveStats.companies, suffix: '+',  color: '#19abb5' },
                { label: 'Success Rate', value: liveStats.success,   suffix: '%',  color: '#10b981' },
                { label: 'Engineers',    value: liveStats.engineers, suffix: '+',  color: '#fdb913' },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <View style={styles.statsDivider} />}
                  <View style={styles.statItem}>
                    <AnimatedCounter target={s.value} suffix={s.suffix} color={s.color} size={18} />
                    <Text style={styles.statLabel}>{s.label}</Text>
                  </View>
                </React.Fragment>
              ))}
            </View>
          </StaggerCard>

          {/* CTA buttons */}
          <StaggerCard delay={150}>
            <View style={styles.ctaRow}>
              <TouchableOpacity style={styles.ctaPrimary} onPress={() => navigate('BusinessLogin')}>
                <MaterialCommunityIcons name="domain" size={18} color="#fff" />
                <Text style={styles.ctaText}>For Business</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => navigate('ProfessionalLogin')}>
                <MaterialCommunityIcons name="account-hard-hat" size={18} color="#fff" />
                <Text style={styles.ctaText}>For Engineers</Text>
              </TouchableOpacity>
            </View>
          </StaggerCard>

          {/* Featured carousel */}
          <StaggerCard delay={200}>
            <Text style={styles.sectionTitle}>Featured</Text>
            <FeaturedCarousel onNavigate={navigate} />
          </StaggerCard>

          {/* Quick actions */}
          <StaggerCard delay={250}>
            <Text style={styles.sectionTitle}>Explore</Text>
            <View style={styles.quickActionsGrid}>
              {QUICK_ACTIONS.map(a => (
                <TouchableOpacity key={a.id} style={styles.quickActionCard} onPress={() => navigate(a.route)} activeOpacity={0.75}>
                  <View style={[styles.quickActionIcon, { backgroundColor: a.color + '18' }]}>
                    <MaterialCommunityIcons name={a.icon} size={24} color={a.color} />
                  </View>
                  <Text style={styles.quickActionLabel}>{a.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </StaggerCard>

          {/* AI Insight of the Day */}
          <StaggerCard delay={300}>
            <View style={styles.aiInsightCard}>
              <View style={styles.aiInsightHeader}>
                <MaterialCommunityIcons name="brain" size={18} color="#0e4e96" />
                <Text style={styles.aiInsightTitle}>AI Insight of the Day</Text>
                <View style={styles.aiPoweredBadge}><Text style={styles.aiPoweredText}>Claude AI</Text></View>
              </View>
              {aiInsight ? (
                <>
                  <View style={styles.aiInsightBody}>
                    <MaterialCommunityIcons name={aiInsight.icon || 'lightbulb-outline'} size={28} color={aiInsight.color || '#0e4e96'} />
                    <Text style={styles.aiInsightText}>{aiInsight.insight}</Text>
                  </View>
                  <TouchableOpacity style={[styles.aiInsightAction, { backgroundColor: (aiInsight.color || '#0e4e96') + '18' }]}>
                    <Text style={[styles.aiInsightActionText, { color: aiInsight.color || '#0e4e96' }]}>→ {aiInsight.action}</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <View style={styles.aiInsightLoading}>
                  <ActivityIndicator size={20} color="#0e4e96" />
                  <Text style={styles.aiInsightLoadingText}>Generating today's insight...</Text>
                </View>
              )}
            </View>
          </StaggerCard>

          {/* Live jobs feed */}
          <StaggerCard delay={350}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Live Job Feed</Text>
              <TouchableOpacity style={styles.seeAllBtn} onPress={() => navigate('SearchJobsScreen')}>
                <Text style={styles.seeAllText}>See all</Text>
                <MaterialIcons name="chevron-right" size={16} color="#0e4e96" />
              </TouchableOpacity>
            </View>

            {liveJobs.length === 0 ? (
              <View style={styles.jobsLoadingCard}>
                <ActivityIndicator size={24} color="#0e4e96" />
                <Text style={styles.jobsLoadingText}>Loading live jobs from Reed...</Text>
              </View>
            ) : (
              liveJobs.slice(0, 4).map((job, i) => (
                <TouchableOpacity key={job.id || i} style={styles.jobCard} onPress={() => navigate(PROFESSIONAL_SIGNUP_ROUTE)} activeOpacity={0.8}>
                  <View style={styles.jobCardLeft}>
                    <Text style={styles.jobTitle} numberOfLines={1}>{job.title}</Text>
                    <Text style={styles.jobCompany}>{job.company}</Text>
                    <View style={styles.jobMetaRow}>
                      <MaterialCommunityIcons name="map-marker-outline" size={12} color="#9ca3af" />
                      <Text style={styles.jobMeta}>{job.location}</Text>
                      <View style={styles.jobMetaDot} />
                      <MaterialCommunityIcons name="currency-gbp" size={12} color="#10b981" />
                      <Text style={[styles.jobMeta, { color: '#10b981', fontWeight: '600' }]}>{job.salary}</Text>
                    </View>
                  </View>
                  <View style={styles.jobCardRight}>
                    <Text style={styles.jobPosted}>{formatDate(job.posted)}</Text>
                    <View style={styles.applyBtn}>
                      <Text style={styles.applyBtnText}>Apply</Text>
                      <MaterialIcons name="open-in-new" size={11} color="#fff" />
                    </View>
                  </View>
                </TouchableOpacity>
              ))
            )}

            <TouchableOpacity style={styles.viewAllJobsBtn} onPress={() => navigate('SearchJobsScreen')}>
              <MaterialCommunityIcons name="briefcase-search" size={16} color="#0e4e96" />
              <Text style={styles.viewAllJobsText}>Browse All UK Engineering Jobs</Text>
              <MaterialIcons name="arrow-forward" size={16} color="#0e4e96" />
            </TouchableOpacity>
          </StaggerCard>

          {/* Trending Skills */}
          <StaggerCard delay={400}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Trending Skills</Text>
              <TouchableOpacity style={styles.seeAllBtn} onPress={() => navigate('UKInsights')}>
                <Text style={styles.seeAllText}>Full report</Text>
                <MaterialIcons name="chevron-right" size={16} color="#0e4e96" />
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 2, paddingBottom: 4 }}>
              {TRENDING_SKILLS.map((sk, i) => (
                <TouchableOpacity key={i} style={[styles.skillChip, { borderColor: sk.color + '44' }]} onPress={() => navigate('UKInsights')}>
                  <MaterialCommunityIcons name={sk.icon} size={16} color={sk.color} />
                  <Text style={styles.skillChipName}>{sk.name}</Text>
                  <Text style={[styles.skillChipGrowth, { color: '#10b981' }]}>{sk.growth}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </StaggerCard>

          {/* Latest News */}
          <StaggerCard delay={450}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Latest News</Text>
              <TouchableOpacity style={styles.seeAllBtn} onPress={() => navigate('NewsList')}>
                <Text style={styles.seeAllText}>All news</Text>
                <MaterialIcons name="chevron-right" size={16} color="#0e4e96" />
              </TouchableOpacity>
            </View>

            {liveNews.length === 0 ? (
              <View style={styles.jobsLoadingCard}>
                <ActivityIndicator size={20} color="#0e4e96" />
                <Text style={styles.jobsLoadingText}>Loading live news...</Text>
              </View>
            ) : (
              liveNews.slice(0, 3).map((article, i) => (
                <TouchableOpacity
                  key={article.id || i}
                  style={styles.newsCard}
                  onPress={() => navigate('NewsDetail', { newsItem: article })}
                  activeOpacity={0.8}
                >
                  <View style={styles.newsCardContent}>
                    {article.priority === 'breaking' && (
                      <View style={styles.breakingPill}>
                        <Text style={styles.breakingPillText}>BREAKING</Text>
                      </View>
                    )}
                    <View style={styles.newsCardMeta}>
                      <Text style={styles.newsCardSource}>{article.source}</Text>
                      <Text style={styles.newsCardDate}>{formatDate(article.publishDate)}</Text>
                    </View>
                    <Text style={styles.newsCardTitle} numberOfLines={2}>{article.title}</Text>
                    <Text style={styles.newsCardSnippet} numberOfLines={2}>{article.snippet}</Text>
                  </View>
                  {article.imageUrl && (
                    <Image source={{ uri: article.imageUrl }} style={styles.newsThumb} />
                  )}
                </TouchableOpacity>
              ))
            )}
          </StaggerCard>

          {/* Academy card */}
          <StaggerCard delay={500}>
            <View style={styles.academyCard}>
              <View style={styles.academyCardLeft}>
                <Text style={styles.academyCardTitle}>🎓 JobEng Academy</Text>
                <Text style={styles.academyCardSubtitle}>AI-powered courses for UK engineers · 50% off this month</Text>
                <TouchableOpacity style={styles.academyCardBtn} onPress={() => navigate('LearningLoginScreen')}>
                  <Image source={require('../assets/jobeng_academy_logo.png')} style={styles.academyLogo} resizeMode="contain" />
                  <Text style={styles.academyCardBtnText}>Start Learning</Text>
                  <Ionicons name="arrow-forward-circle" size={20} color="#fff" />
                </TouchableOpacity>
              </View>
              <MaterialCommunityIcons name="school" size={60} color="rgba(255,255,255,0.2)" />
            </View>
          </StaggerCard>

          {/* UK Insights banner */}
          <StaggerCard delay={550}>
            <View style={styles.insightsBanner}>
              <View style={styles.insightsBannerLeft}>
                <Text style={styles.insightsBannerTitle}>🇬🇧 UK Engineering Insights</Text>
                <Text style={styles.insightsBannerText}>
                  Real-time salary data, job market trends, regional analysis, and AI-powered career intelligence for UK engineers.
                </Text>
                <TouchableOpacity style={styles.insightsBannerBtn} onPress={() => navigate('UKInsights')}>
                  <Text style={styles.insightsBannerBtnText}>View Live Dashboard</Text>
                  <MaterialIcons name="arrow-forward" size={14} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              <View style={styles.insightsBannerStats}>
                {[
                  { label: 'Jobs', value: '15K+', color: '#fdb913' },
                  { label: 'Avg Salary', value: '£52.5k', color: '#10b981' },
                  { label: 'Growth', value: '+12.5%', color: '#19abb5' },
                ].map(s => (
                  <View key={s.label} style={styles.insightsMiniStat}>
                    <Text style={[styles.insightsMiniValue, { color: s.color }]}>{s.value}</Text>
                    <Text style={styles.insightsMiniLabel}>{s.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </StaggerCard>

          {/* Salary comparison teaser */}
          <StaggerCard delay={600}>
            <TouchableOpacity style={styles.salaryTeaser} onPress={() => navigate('SalaryComparison')} activeOpacity={0.85}>
              <View>
                <Text style={styles.salaryTeaserTitle}>💷 Is Your Salary Competitive?</Text>
                <Text style={styles.salaryTeaserSubtitle}>Compare your pay against 12 engineering disciplines across all UK regions</Text>
              </View>
              <View style={styles.salaryTeaserRight}>
                <MaterialCommunityIcons name="scale-balance" size={36} color="rgba(255,255,255,0.3)" />
                <Text style={styles.salaryTeaserCta}>Check Now →</Text>
              </View>
            </TouchableOpacity>
          </StaggerCard>

          {/* Footer info */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>JOBENG v{APP_VERSION} · UK Engineering Platform</Text>
            <Text style={styles.footerSubtext}>Data: Guardian API · Reed Jobs API · ONS Statistics</Text>
          </View>

        </Animated.View>
      </ScrollView>

      {/* ── DEVELOPER PANEL ─────────────────────────────────────────────────── */}
      {devMode && (
        <Modal visible={showDevPanel} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDevPanel(false)}>
          <View style={styles.devPanelContainer}>
            <View style={styles.devPanelHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons name="construct" size={22} color="#6366F1" />
                <Text style={styles.devPanelTitle}>Developer Panel</Text>
                <View style={{ backgroundColor: '#EF4444', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6 }}>
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800' }}>v{APP_VERSION}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setShowDevPanel(false)} style={styles.devPanelCloseButton}>
                <Ionicons name="close" size={28} color="#1F2937" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.devPanelContent} showsVerticalScrollIndicator={false}>

              {/* App Info */}
              <View style={styles.devSection}>
                <Text style={styles.devSectionTitle}>📱 App Info</Text>
                {[
                  ['Version', APP_VERSION], ['Build', 'Development'], ['Platform', `${Platform.OS} ${Platform.Version}`],
                  ['Live Data', isLive ? '✅ Connected' : '❌ Offline'], ['Last Update', lastUpdated || 'N/A'],
                  ['News Articles', liveNews.length], ['Live Jobs', liveJobs.length], ['Alert Count', alertCount],
                ].map(([k, v]) => (
                  <View key={k} style={styles.devInfoRow}>
                    <Text style={styles.devInfoLabel}>{k}</Text>
                    <Text style={styles.devInfoValue}>{v}</Text>
                  </View>
                ))}
              </View>

              {/* Settings toggles */}
              {[
                { title: '🌐 API & Network',         keys: ['showAPILogs', 'mockDataEnabled', 'bypassAPIErrors', 'slowNetworkSimulation', 'offlineMode'] },
                { title: '🔒 Auth & Security',       keys: ['bypassAuth', 'autoLogin', 'skipOnboarding', 'disableTokenRefresh'] },
                { title: '🎨 UI & UX',               keys: ['debugMode', 'showBoundingBoxes', 'disableAnimations', 'showPerformanceMetrics', 'forceRTL'] },
                { title: '💾 Data & Storage',        keys: ['clearCacheOnStart', 'persistLogs', 'enableDataExport'] },
                { title: '🧪 Features & Testing',    keys: ['enableBetaFeatures', 'enableExperimentalUI', 'disableAnalytics', 'disableCrashReporting', 'enableTestMode', 'mockPayments', 'skipValidation'] },
              ].map(section => (
                <View key={section.title} style={styles.devSection}>
                  <Text style={styles.devSectionTitle}>{section.title}</Text>
                  {section.keys.map(key => (
                    <View key={key} style={styles.devSettingRow}>
                      <Text style={styles.devSettingLabel}>{key.replace(/([A-Z])/g, ' $1').trim()}</Text>
                      <Switch value={devSettings[key]} onValueChange={() => toggleDevSetting(key)}
                        trackColor={{ false: '#D1D5DB', true: '#6366F1' }} thumbColor={devSettings[key] ? '#fff' : '#F3F4F6'} />
                    </View>
                  ))}
                </View>
              ))}

              {/* Actions */}
              <View style={styles.devSection}>
                <Text style={styles.devSectionTitle}>⚡ Actions</Text>
                {[
                  { label: 'View Storage Data',   icon: 'folder-open-outline', color: '#6366F1', fn: viewStorageData },
                  { label: 'Test API Connections',icon: 'wifi-outline',         color: '#10B981', fn: testAPIConnection },
                  { label: 'Export Logs',          icon: 'document-outline',    color: '#6366F1', fn: exportLogs },
                  { label: 'Share Debug Info',     icon: 'share-outline',       color: '#0e4e96', fn: shareDebugInfo },
                  { label: 'Clear All Data',       icon: 'trash-outline',       color: '#EF4444', fn: clearAllData },
                  { label: 'Factory Reset',        icon: 'refresh-outline',     color: '#F59E0B', fn: resetToFactory },
                  { label: 'Force Crash',          icon: 'warning-outline',     color: '#EF4444', fn: forceCrash },
                ].map(a => (
                  <TouchableOpacity key={a.label} style={styles.devActionButton} onPress={a.fn}>
                    <Ionicons name={a.icon} size={20} color={a.color} />
                    <Text style={[styles.devActionText, { color: a.color }]}>{a.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <View style={{ height: 20 }} />
            </ScrollView>

            <View style={styles.devPanelFooter}>
              <TouchableOpacity style={styles.devSaveButton} onPress={saveDevSettings}>
                <Ionicons name="save-outline" size={18} color="#fff" style={{ marginRight: 8 }} />
                <Text style={styles.devSaveButtonText}>Save Settings</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      )}

      {/* Dev mode floating badge */}
      {devMode && (
        <View style={styles.devModeIndicator}>
          <TouchableOpacity onPress={() => setShowDevPanel(true)} style={styles.devModeButton}>
            <Ionicons name="construct" size={14} color="#fff" />
            <Text style={styles.devModeText}>DEV MODE</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  // ── Header ──────────────────────────────────────────────────────────────────
  headerBackground:  { backgroundColor: '#0e4e96', paddingTop: Platform.OS === 'ios' ? 50 : 36, paddingBottom: 16 },
  headerTopRow:      { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, marginBottom: 10 },
  logoWrap:          { marginRight: 10 },
  logo:              { width: 42, height: 42, borderRadius: 10 },
  headerCenterCol:   { flex: 1 },
  title:             { fontSize: 26, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },
  liveRow:           { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 1 },
  liveDot:           { width: 7, height: 7, borderRadius: 4 },
  liveLabel:         { fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: '600', letterSpacing: 0.5 },
  headerRight:       { flexDirection: 'row', gap: 4 },
  headerIconBtn:     { padding: 8, position: 'relative' },
  notifBadge:        { position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: 8, backgroundColor: '#ef4444', justifyContent: 'center', alignItems: 'center' },
  notifBadgeText:    { color: '#fff', fontSize: 8, fontWeight: '900' },
  subtitle:          { color: 'rgba(255,255,255,0.8)', textAlign: 'center', fontSize: 13, marginBottom: 12 },
  // Search bar
  searchWrap:        { flexDirection: 'row', paddingHorizontal: 16, marginBottom: 12, gap: 8 },
  searchBar:         { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, paddingHorizontal: 12, height: 42, gap: 8, borderWidth: 1.5, borderColor: 'transparent' },
  searchBarFocused:  { borderColor: '#fdb913' },
  searchInput:       { flex: 1, fontSize: 14, color: '#374151', height: '100%' },
  searchBtn:         { backgroundColor: '#fdb913', paddingHorizontal: 14, borderRadius: 12, justifyContent: 'center', height: 42 },
  searchBtnText:     { color: '#fff', fontWeight: '700', fontSize: 13 },
  // Ticker
  tickerWrap:        { flexDirection: 'row', alignItems: 'center', marginHorizontal: 16, backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10, overflow: 'hidden' },
  tickerLabel:       { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 8, borderRightWidth: 1, borderRightColor: 'rgba(255,255,255,0.3)', paddingRight: 8 },
  tickerLabelText:   { color: '#fdb913', fontSize: 9, fontWeight: '900', letterSpacing: 1 },
  tickerTrack:       { flex: 1, overflow: 'hidden' },
  tickerText:        { color: '#fff', fontSize: 13, width: 3000 },
  // Content container
  container:         { backgroundColor: '#f4f8fb', padding: 16, paddingTop: 12, paddingBottom: 30 },
  // Welcome card
  welcomeCard:       { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 16, padding: 18, marginBottom: 14, shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6, elevation: 3 },
  welcomeLeft:       { flex: 1 },
  welcomeGreeting:   { fontSize: 17, fontWeight: '800', color: '#1f2937', marginBottom: 4 },
  welcomeSubtext:    { fontSize: 13, color: '#6b7280', marginBottom: 10, lineHeight: 19 },
  welcomeTagRow:     { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  welcomeTag:        { backgroundColor: '#f0f4ff', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  welcomeTagText:    { fontSize: 11, color: '#0e4e96', fontWeight: '600' },
  welcomeIconWrap:   { width: 60, height: 60, borderRadius: 16, backgroundColor: '#f0f4ff', justifyContent: 'center', alignItems: 'center', marginLeft: 10 },
  // Stats
  statsCard:         { flexDirection: 'row', backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 14, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  statItem:          { flex: 1, alignItems: 'center' },
  statLabel:         { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  statsDivider:      { width: 1, backgroundColor: '#f3f4f6', alignSelf: 'stretch', marginHorizontal: 4 },
  // CTA row
  ctaRow:            { flexDirection: 'row', gap: 10, marginBottom: 20 },
  ctaPrimary:        { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#10b981', paddingVertical: 14, borderRadius: 14, gap: 8, shadowColor: '#10b981', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.3, shadowRadius: 5, elevation: 3 },
  ctaSecondary:      { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', paddingVertical: 14, borderRadius: 14, gap: 8, shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.3, shadowRadius: 5, elevation: 3 },
  ctaText:           { color: '#fff', fontSize: 15, fontWeight: '700' },
  // Section title
  sectionTitle:      { fontSize: 18, fontWeight: '800', color: '#1f2937', marginBottom: 12 },
  sectionHeader:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  seeAllBtn:         { flexDirection: 'row', alignItems: 'center', gap: 2 },
  seeAllText:        { fontSize: 13, color: '#0e4e96', fontWeight: '600' },
  // Featured carousel
  carouselContainer: { marginBottom: 20 },
  carouselCard:      { borderRadius: 18, overflow: 'hidden', shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 5 },
  carouselBg:        { padding: 20 },
  carouselInner:     { flexDirection: 'row', alignItems: 'center' },
  carouselTopRow:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  carouselBadge:     { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  carouselBadgeText: { color: '#fff', fontSize: 10, fontWeight: '900', letterSpacing: 1 },
  carouselDots:      { flexDirection: 'row', gap: 5 },
  carouselDot:       { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.35)' },
  carouselDotActive: { backgroundColor: '#fff', width: 14 },
  carouselTitle:     { fontSize: 19, fontWeight: '800', color: '#fff', marginBottom: 6, lineHeight: 25 },
  carouselDescription:{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 19, marginBottom: 14 },
  carouselButton:    { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fdb913', paddingVertical: 9, paddingHorizontal: 16, borderRadius: 10, alignSelf: 'flex-start', gap: 6 },
  carouselButtonText:{ color: '#fff', fontWeight: '700', fontSize: 13 },
  carouselIconWrap:  { marginLeft: 12 },
  // Quick actions
  quickActionsGrid:  { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 },
  quickActionCard:   { width: (width - 32 - 30) / 4, alignItems: 'center', gap: 6 },
  quickActionIcon:   { width: 52, height: 52, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  quickActionLabel:  { fontSize: 11, fontWeight: '600', color: '#374151', textAlign: 'center' },
  // AI Insight card
  aiInsightCard:     { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 20, borderLeftWidth: 3, borderLeftColor: '#0e4e96', shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  aiInsightHeader:   { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 },
  aiInsightTitle:    { fontSize: 14, fontWeight: '700', color: '#1f2937', flex: 1 },
  aiPoweredBadge:    { backgroundColor: '#e8f0fe', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  aiPoweredText:     { fontSize: 10, color: '#0e4e96', fontWeight: '700' },
  aiInsightBody:     { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  aiInsightText:     { flex: 1, fontSize: 13, color: '#374151', lineHeight: 20 },
  aiInsightAction:   { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, alignSelf: 'flex-start' },
  aiInsightActionText:{ fontSize: 12, fontWeight: '700' },
  aiInsightLoading:  { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8 },
  aiInsightLoadingText: { fontSize: 13, color: '#9ca3af' },
  // Job cards
  jobsLoadingCard:   { backgroundColor: '#fff', borderRadius: 14, padding: 20, alignItems: 'center', flexDirection: 'row', gap: 12, marginBottom: 10 },
  jobsLoadingText:   { fontSize: 13, color: '#9ca3af' },
  jobCard:           { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, flexDirection: 'row', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  jobCardLeft:       { flex: 1 },
  jobTitle:          { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 3 },
  jobCompany:        { fontSize: 13, color: '#0e4e96', fontWeight: '500', marginBottom: 5 },
  jobMetaRow:        { flexDirection: 'row', alignItems: 'center', gap: 3 },
  jobMeta:           { fontSize: 11, color: '#9ca3af' },
  jobMetaDot:        { width: 3, height: 3, borderRadius: 2, backgroundColor: '#d1d5db', marginHorizontal: 2 },
  jobCardRight:      { alignItems: 'flex-end', gap: 8 },
  jobPosted:         { fontSize: 10, color: '#9ca3af' },
  applyBtn:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8, gap: 4 },
  applyBtnText:      { color: '#fff', fontSize: 11, fontWeight: '700' },
  viewAllJobsBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f4ff', paddingVertical: 14, borderRadius: 12, gap: 8, marginTop: 4, borderWidth: 1.5, borderColor: '#0e4e96' },
  viewAllJobsText:   { fontSize: 14, color: '#0e4e96', fontWeight: '700' },
  // Trending skills
  skillChip:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, marginRight: 8, gap: 6, borderWidth: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 2, elevation: 1 },
  skillChipName:     { fontSize: 12, fontWeight: '600', color: '#374151' },
  skillChipGrowth:   { fontSize: 11, fontWeight: '700' },
  // News cards
  newsCard:          { flexDirection: 'row', backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, gap: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  newsCardContent:   { flex: 1 },
  breakingPill:      { backgroundColor: '#dc2626', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 5, alignSelf: 'flex-start', marginBottom: 5 },
  breakingPillText:  { color: '#fff', fontSize: 9, fontWeight: '900', letterSpacing: 0.5 },
  newsCardMeta:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  newsCardSource:    { fontSize: 10, fontWeight: '700', color: '#0e4e96', textTransform: 'uppercase' },
  newsCardDate:      { fontSize: 10, color: '#9ca3af' },
  newsCardTitle:     { fontSize: 13, fontWeight: '700', color: '#1f2937', lineHeight: 18, marginBottom: 4 },
  newsCardSnippet:   { fontSize: 12, color: '#6b7280', lineHeight: 17 },
  newsThumb:         { width: 72, height: 72, borderRadius: 10 },
  // Academy card
  academyCard:       { flexDirection: 'row', alignItems: 'center', backgroundColor: '#007AFF', borderRadius: 16, padding: 20, marginBottom: 16, shadowColor: '#007AFF', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.3, shadowRadius: 6, elevation: 4 },
  academyCardLeft:   { flex: 1 },
  academyCardTitle:  { fontSize: 17, fontWeight: '800', color: '#fff', marginBottom: 4 },
  academyCardSubtitle:{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginBottom: 14, lineHeight: 19 },
  academyCardBtn:    { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 12, gap: 8, alignSelf: 'flex-start' },
  academyLogo:       { width: 24, height: 24, borderRadius: 5 },
  academyCardBtnText:{ color: '#fff', fontWeight: '700', fontSize: 14 },
  // UK Insights banner
  insightsBanner:    { backgroundColor: '#fff', borderRadius: 16, padding: 18, marginBottom: 14, borderLeftWidth: 4, borderLeftColor: '#fdb913', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  insightsBannerLeft:{},
  insightsBannerTitle:{ fontSize: 16, fontWeight: '800', color: '#1f2937', marginBottom: 6 },
  insightsBannerText:{ fontSize: 13, color: '#6b7280', lineHeight: 19, marginBottom: 14 },
  insightsBannerBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 10, alignSelf: 'flex-start', gap: 6, marginBottom: 14 },
  insightsBannerBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
  insightsBannerStats:{ flexDirection: 'row', gap: 16 },
  insightsMiniStat:  { alignItems: 'center' },
  insightsMiniValue: { fontSize: 15, fontWeight: '800' },
  insightsMiniLabel: { fontSize: 10, color: '#9ca3af', marginTop: 1 },
  // Salary teaser
  salaryTeaser:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1e3a5f', borderRadius: 16, padding: 18, marginBottom: 14 },
  salaryTeaserTitle: { fontSize: 15, fontWeight: '800', color: '#fff', marginBottom: 5 },
  salaryTeaserSubtitle: { fontSize: 12, color: 'rgba(255,255,255,0.75)', maxWidth: '75%', lineHeight: 17 },
  salaryTeaserRight: { alignItems: 'center', gap: 8 },
  salaryTeaserCta:   { color: '#fdb913', fontWeight: '800', fontSize: 13 },
  // Footer
  footer:            { alignItems: 'center', paddingTop: 10 },
  footerText:        { fontSize: 11, color: '#9ca3af', fontWeight: '600' },
  footerSubtext:     { fontSize: 10, color: '#c4c4c4', marginTop: 2 },
  // ── Developer Panel ────────────────────────────────────────────────────────
  devPanelContainer: { flex: 1, backgroundColor: '#F9FAFB' },
  devPanelHeader:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  devPanelTitle:     { fontSize: 18, fontWeight: '700', color: '#1F2937' },
  devPanelCloseButton:{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  devPanelContent:   { flex: 1, paddingHorizontal: 16 },
  devSection:        { marginTop: 16, backgroundColor: '#fff', borderRadius: 12, padding: 16 },
  devSectionTitle:   { fontSize: 14, fontWeight: '700', color: '#1F2937', marginBottom: 12 },
  devSettingRow:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  devSettingLabel:   { fontSize: 13, color: '#6B7280', flex: 1, textTransform: 'capitalize' },
  devActionButton:   { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', gap: 10 },
  devActionText:     { fontSize: 14, fontWeight: '600', color: '#6366F1' },
  devInfoRow:        { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#F9FAFB' },
  devInfoLabel:      { fontSize: 13, color: '#6B7280' },
  devInfoValue:      { fontSize: 13, fontWeight: '700', color: '#1F2937' },
  devPanelFooter:    { padding: 16, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB' },
  devSaveButton:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#6366F1', paddingVertical: 14, borderRadius: 12 },
  devSaveButtonText: { fontSize: 15, fontWeight: '700', color: '#fff' },
  devModeIndicator:  { position: 'absolute', bottom: 24, right: 20, zIndex: 1000 },
  devModeButton:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#EF4444', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, gap: 5, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.25, shadowRadius: 4, elevation: 6 },
  devModeText:       { fontSize: 11, fontWeight: '800', color: '#fff', letterSpacing: 0.5 },
});